import { useAuth } from '@/contexts/AuthContext';
import { useShouldRenderAds } from '@/lib/adsConfig';
import { useTestVariant } from '@/lib/abTesting';
import { AdSense, ResponsiveDisplayAd } from './AdSense';

interface AdSlotProps {
  slot: 'leaderboard' | 'sidebar' | 'in-content' | 'mobile-banner' | 'responsive-display';
  className?: string;
}

// Maps semantic slot names to the real AdSense ad-unit slot IDs (see AdSense.tsx)
// plus a CLS-safe min-height wrapper. Previously AdSlot rendered an empty
// placeholder <div> that never produced a real <ins>, so it never served an ad.
const SLOT_CONFIG: Record<
  AdSlotProps['slot'],
  { slotId: string; format: 'auto' | 'rectangle' | 'vertical' | 'horizontal'; wrapper: string }
> = {
  leaderboard: { slotId: '8985994760', format: 'auto', wrapper: 'min-h-[90px]' },
  sidebar: { slotId: '8395215240', format: 'auto', wrapper: 'hidden lg:block min-h-[250px]' },
  'in-content': { slotId: '1498672391', format: 'auto', wrapper: 'min-h-[250px]' },
  'mobile-banner': { slotId: '1498672391', format: 'auto', wrapper: 'min-h-[50px] block md:hidden' },
  'responsive-display': { slotId: '', format: 'auto', wrapper: '' },
};

// Secondary placements that get suppressed in the lighter ad-density variant.
const SECONDARY_SLOTS: AdSlotProps['slot'][] = ['in-content', 'mobile-banner'];

const AdSlot = ({ slot, className = '' }: AdSlotProps) => {
  const { hasUnlimitedAccess } = useAuth();
  const density = useTestVariant('ad-density-test');
  const shouldRender = useShouldRenderAds();

  // Legacy Pro/admin users keep an ad-free experience (no reserved gap).
  if (hasUnlimitedAccess) {
    return null;
  }

  if (!shouldRender) {
    return null;
  }

  // Density tuning: drop secondary ad units in the lighter variant.
  if (density === 'minimal' && SECONDARY_SLOTS.includes(slot)) {
    return null;
  }

  if (slot === 'responsive-display') {
    return <ResponsiveDisplayAd className={className} />;
  }

  const config = SLOT_CONFIG[slot];

  return (
    <AdSense
      slot={config.slotId}
      format={config.format}
      className={`${config.wrapper} ${className}`}
    />
  );
};

export default AdSlot;
